import React, { useState, useEffect, useRef } from 'react'
import {
  Plus,
  Calendar,
  Users,
  CheckCircle,
  Clock,
  TrendingUp
} from 'lucide-react'
import DataTable from '../../../components/DataTable/DataTable'
import Breadcrumb from '../../../components/Common/Breadcrumb'
import ConfirmationModal from '../../../components/ConfirmationModal/ConfirmationModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import resourceCalendarService from '../../../services/resourceCalendarService'
import '../../../components/ConfirmationModal/ConfirmationModal.css'

const employeeName = (row) => {
  const emp = row.employee
  if (!emp) return '—'
  if (typeof emp === 'object') {
    return emp.employeeName || `${emp.firstName || ''} ${emp.lastName || ''}`.trim() || '—'
  }
  return '—'
}

const projectName = (row) => {
  const p = row.project
  if (!p) return '—'
  if (typeof p === 'object') return p.projectName || p.projectNumber || '—'
  return '—'
}

const formatDate = (d) => {
  if (!d) return '—'
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
}

const ResourceCalendar = () => {
  const navigate = useNavigate()
  const [feedData, setFeedData] = useState([])
  const [eventsData, setEventsData] = useState([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({
    total: 0,
    scheduled: 0,
    confirmed: 0,
    thisMonth: 0,
    allocations: 0
  })
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteItem, setDeleteItem] = useState(null)
  const hasLoadedRef = useRef(false)

  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true
      loadData()
    }
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [feedRes, eventsRes, statsRes] = await Promise.all([
        resourceCalendarService.getFeed(),
        resourceCalendarService.getAll({ limit: 500 }),
        resourceCalendarService.getStats()
      ])

      if (feedRes?.success && feedRes.data) {
        const items = (feedRes.data.items || []).map((item, idx) => ({
          ...item,
          id: item._id || `feed-${idx}`,
          feedKey: `${item.source}-${item._id || idx}`
        }))
        setFeedData(items)
      }

      if (eventsRes?.success) {
        setEventsData((eventsRes.events || []).map((e) => ({ ...e, id: e._id })))
      }

      if (statsRes?.success) {
        setStats(statsRes.data || {})
      }
    } catch (error) {
      console.error(error)
      toast.error('Failed to load resource calendar')
    } finally {
      setLoading(false)
    }
  }

  const sourceBadge = (source) => {
    const isAlloc = source === 'allocation'
    const color = isAlloc ? '#222448' : '#6A4CA2'
    return (
      <span
        className="inline-flex items-center px-2.5 py-0.5 text-xs font-medium"
        style={{
          backgroundColor: `${color}18`,
          color,
          border: `1px solid ${color}40`
        }}
      >
        {isAlloc ? 'Allocation' : 'Calendar'}
      </span>
    )
  }

  const feedColumns = [
    {
      key: 'source',
      label: 'Source',
      sortable: true,
      render: (value) => sourceBadge(value || 'calendar')
    },
    {
      key: 'eventNumber',
      label: 'Ref #',
      sortable: true,
      render: (value, row) => value || row.allocationNumber || '—'
    },
    {
      key: 'title',
      label: 'Title',
      sortable: true,
      render: (value) => (
        <div style={{ maxWidth: '240px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {value || '—'}
        </div>
      )
    },
    {
      key: 'employee',
      label: 'Employee',
      sortable: false,
      render: (_, row) => employeeName(row)
    },
    {
      key: 'project',
      label: 'Project',
      sortable: false,
      render: (_, row) => projectName(row)
    },
    {
      key: 'eventType',
      label: 'Type',
      sortable: true,
      render: (value) => value || '—'
    },
    {
      key: 'startDate',
      label: 'Start',
      sortable: true,
      render: (value) => formatDate(value)
    },
    {
      key: 'endDate',
      label: 'End',
      sortable: true,
      render: (value) => formatDate(value)
    },
    {
      key: 'allocationPercent',
      label: 'Alloc %',
      sortable: true,
      render: (value) => (value != null ? `${value}%` : '—')
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (value) => value || '—'
    }
  ]

  const eventColumns = [
    {
      key: 'eventNumber',
      label: 'Event #',
      sortable: true,
      render: (value) => value || '—'
    },
    {
      key: 'title',
      label: 'Title',
      sortable: true,
      render: (value) => value || '—'
    },
    {
      key: 'employee',
      label: 'Employee',
      sortable: false,
      render: (_, row) => employeeName(row)
    },
    {
      key: 'eventType',
      label: 'Type',
      sortable: true,
      render: (value) => value || '—'
    },
    {
      key: 'startDate',
      label: 'Start',
      sortable: true,
      render: (value) => formatDate(value)
    },
    {
      key: 'endDate',
      label: 'End',
      sortable: true,
      render: (value) => formatDate(value)
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (value) => value || '—'
    }
  ]

  const handleFeedView = (row) => {
    if (row.source === 'allocation') {
      navigate(`/projects/resource-allocations/view/${row._id}`)
      return
    }
    navigate(`/projects/resource-calendar/view/${row._id}`)
  }

  const handleFeedEdit = (row) => {
    if (row.source === 'allocation') {
      toast.info('Edit this item from Resource Allocation')
      navigate(`/projects/resource-allocations/edit/${row._id}`)
      return
    }
    navigate(`/projects/resource-calendar/edit/${row._id}`)
  }

  const breadcrumbItems = [
    { label: 'Projects', path: '/projects', onClick: () => navigate('/projects') },
    { label: 'Transactions', path: '/projects?tab=transactions', onClick: () => navigate('/projects?tab=transactions') },
    { label: 'Resource Calendar', path: '/projects/resource-calendar', isCurrent: true }
  ]

  return (
    <div className="sales-dashboard">
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <div className="page-header">
        <div className="page-title">
          <h1>Resource Calendar</h1>
          <p>Combined schedule of calendar events and resource allocations</p>
        </div>
        <div className="page-actions">
          <button type="button" className="btn-primary" onClick={() => navigate('/projects/resource-calendar/new')}>
            <Plus size={16} />
            Add Event
          </button>
        </div>
      </div>

      <div className="dashboard-metrics-grid">
        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon"><Calendar size={16} /></div>
            <div className="metric-trend positive"><TrendingUp size={12} /><span>Events</span></div>
          </div>
          <div className="metric-value">{stats.total || 0}</div>
          <div className="metric-title">Calendar Events</div>
          <div className="metric-subtitle">{stats.thisMonth || 0} this month</div>
        </div>
        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon"><Clock size={16} /></div>
            <div className="metric-trend neutral"><TrendingUp size={12} /><span>Plan</span></div>
          </div>
          <div className="metric-value">{stats.scheduled || 0}</div>
          <div className="metric-title">Scheduled</div>
          <div className="metric-subtitle">Upcoming</div>
        </div>
        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon"><CheckCircle size={16} /></div>
            <div className="metric-trend positive"><TrendingUp size={12} /><span>OK</span></div>
          </div>
          <div className="metric-value">{stats.confirmed || 0}</div>
          <div className="metric-title">Confirmed</div>
          <div className="metric-subtitle">Locked in</div>
        </div>
        <div className="metric-card burn">
          <div className="metric-header">
            <div className="metric-icon"><Users size={16} /></div>
            <div className="metric-trend positive"><TrendingUp size={12} /><span>Alloc</span></div>
          </div>
          <div className="metric-value">{stats.allocations || 0}</div>
          <div className="metric-title">Active Allocations</div>
          <div className="metric-subtitle">Merged in feed</div>
        </div>
      </div>

      <div className="dashboard-content" style={{ marginBottom: '24px' }}>
        <div className="section-header" style={{ marginBottom: '12px' }}>
          <h3 style={{ margin: 0, color: '#222448' }}>Calendar Feed</h3>
          <p style={{ margin: '4px 0 0', fontSize: '13px', color: 'var(--color-text-secondary, #5B6478)' }}>
            Calendar events and resource allocations for the current range
          </p>
        </div>
        <DataTable
          data={feedData}
          columns={feedColumns}
          loading={loading}
          onView={handleFeedView}
          onEdit={handleFeedEdit}
          onDelete={(row) => {
            if (row.source === 'allocation') {
              toast.info('Delete allocations from Resource Allocation module')
              return
            }
            setDeleteItem(row)
            setShowDeleteModal(true)
          }}
          searchPlaceholder="Search feed..."
          emptyMessage="No calendar items in this range."
          noCard={true}
        />
      </div>

      <div className="dashboard-content">
        <div className="section-header" style={{ marginBottom: '12px' }}>
          <h3 style={{ margin: 0, color: '#222448' }}>Calendar Events Only</h3>
        </div>
        <DataTable
          data={eventsData}
          columns={eventColumns}
          loading={loading}
          onEdit={(row) => navigate(`/projects/resource-calendar/edit/${row._id || row.id}`)}
          onView={(row) => navigate(`/projects/resource-calendar/view/${row._id || row.id}`)}
          onDelete={(row) => {
            setDeleteItem(row)
            setShowDeleteModal(true)
          }}
          searchPlaceholder="Search events..."
          emptyMessage="No calendar events yet."
          noCard={true}
        />
      </div>

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={async () => {
          if (!deleteItem) return
          try {
            const res = await resourceCalendarService.remove(deleteItem._id || deleteItem.id)
            if (res.success) {
              toast.success('Event deleted')
              loadData()
            } else {
              toast.error(res.message || 'Delete failed')
            }
          } catch {
            toast.error('Failed to delete event')
          } finally {
            setShowDeleteModal(false)
            setDeleteItem(null)
          }
        }}
        title="Delete Calendar Event"
        message={`Delete "${deleteItem?.title || deleteItem?.eventNumber || ''}"?`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  )
}

export default ResourceCalendar
